import React from 'react';
import {
  ClockIcon,
  MailIcon,
  PhoneIcon,
  TinyTitleBold,
  Caption,
  BodyText
} from '@kvdbil/components';
import styled from 'styled-components';
import { ContactDepartmentCardRecord } from '~/config/generated/graphql';
import { useSelector } from 'react-redux';
import { localizationSelector } from '~/App/shared/selectors/localization';
import Link from '~/App/shared/components/Link';
import WeeklyCustomerService from '~/App/shared/components/WeeklyCustomerService';
import {
  formatPhoneNumberForLink,
  formatPhoneNumber
} from '~/helpers/phoneNumber';

const Card = styled.div<{ $showDetailedOpeningHours: boolean }>`
  background: ${({ theme, $showDetailedOpeningHours }) =>
    $showDetailedOpeningHours
      ? theme.colors.background.lightGray
      : theme.colors.background.light};
  border: 2px solid ${({ theme }) => theme.colors.background.gray};
  border-radius: 8px;
  overflow: hidden;
  height: fit-content;
`;

const Header = styled.div<{ $showDetailedOpeningHours: boolean }>`
  background: ${({ theme }) => theme.colors.background.lightGray};
  padding: 1rem;
  ${({ $showDetailedOpeningHours }) =>
    $showDetailedOpeningHours && 'padding-bottom: 0;'}
`;

const List = styled.div`
  padding: 1rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  padding-top: 0.5rem;
`;

const RowContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  height: 1px;
  width: 100%;
  background: ${({ theme }) => theme.colors.background.gray};
`;

type Props = {
  item: ContactDepartmentCardRecord;
};

const ContactDepartmentCard = ({ item }: Props) => {
  const { domainLocale } = useSelector(localizationSelector);
  const {
    name,
    phone,
    email,
    openingHours,
    extraDescriptiveLine,
    showDetailedOpeningHours
  } = item;
  const formattedPhone = formatPhoneNumberForLink(phone ?? '');
  const displayPhone =
    domainLocale !== 'sv' ? formatPhoneNumber(formattedPhone) : phone;

  return (
    <Card $showDetailedOpeningHours={showDetailedOpeningHours}>
      <Header $showDetailedOpeningHours={showDetailedOpeningHours}>
        {name && <TinyTitleBold>{name}</TinyTitleBold>}
        {extraDescriptiveLine && <Caption>{extraDescriptiveLine}</Caption>}
      </Header>
      <List>
        {phone && (
          <Row>
            <RowContent>
              <PhoneIcon width={24} height={24} />
              <Link to={`tel:${formattedPhone}`} withUnderline>
                {displayPhone}
              </Link>
            </RowContent>
            <Divider />
          </Row>
        )}
        {email && (
          <Row>
            <RowContent>
              <MailIcon width={24} height={24} />
              <Link to={`mailto:${email}`} withUnderline>
                {email}
              </Link>
            </RowContent>
            <Divider />
          </Row>
        )}
        {openingHours && (
          <Row>
            <RowContent>
              <ClockIcon width={24} height={24} />
              <BodyText as="span">{openingHours}</BodyText>
            </RowContent>
            <Divider />
          </Row>
        )}
        {showDetailedOpeningHours && (
          <WeeklyCustomerService onlyDays isExtendable />
        )}
      </List>
    </Card>
  );
};

export default ContactDepartmentCard;
